import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { UserInfo } from "../../../../shared/types/user-info";
import { ConfigurableOptionService } from "../configurable-options.service";
import { ConfigurableOption } from "../configurable-options.type";
import {
  CreateConfigurableOptionService,
  ICreateConfigurableOptionForm,
} from "../create-configurable-option/create-configurable-option.service";

@Component({
  selector: "app-manage-configurable-option",
  templateUrl: "./manage-configurable-option.component.html",
  styleUrls: ["./manage-configurable-option.component.scss"],
  providers: [CreateConfigurableOptionService],
})
export class ManageConfigurableOptionComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  currentUser: UserInfo;
  loading = false;
  configurableOption: ConfigurableOption;
  isEdit: boolean;
  questions: QuestionBase<string | boolean>[];

  constructor(
    public dialog: MatDialog,
    private loader: LoadingService,
    private route: ActivatedRoute,
    private router: Router,
    public location: Location,
    private configurableOptionService: ConfigurableOptionService,
    private questionService: CreateConfigurableOptionService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.loader.show();
  }

  ngOnInit(): void {
    const configurableOptionId: string = this.route.snapshot.paramMap.get("id");
    this.getConfigurableOption(configurableOptionId);
  }

  goBack(): void {
    this.location.back();
  }

  getConfigurableOption(id: string): Subscription {
    return this.configurableOptionService.getConfigurableOption(id).subscribe(
      (configurableOption: ConfigurableOption) => {
        this.configurableOption = configurableOption;
        this.buildForm(configurableOption);
      },
      (error) => {
        this.notificationService.error(error || "Couldn't find configurable option");
        this.router.navigate(["/configurable-options"]);
      },
    );
  }

  buildForm(configurableOption: ConfigurableOption): void {
    this.questionService
      .generateQuestions({
        value: configurableOption.value,
        type: configurableOption.type,
      })
      .subscribe((qs) => {
        this.questions = qs;
        this.loader.hide();
      });
  }

  toggleManageConfig(): void {
    this.isEdit = !this.isEdit;
  }

  onSubmit(values: ICreateConfigurableOptionForm): void {
    this.loader.show();
    this.configurableOptionService
      .updateConfigurableOption(this.configurableOption.id, { ...values })
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (configurableOption): void => {
          this.notificationService.success("Configurable Option successfully edited");
          this.configurableOption = configurableOption;
          this.toggleManageConfig();
          this.loader.hide();
        },
        (error) => {
          this.loader.hide();
          this.notificationService.error(error?.message ?? "Could not create configurable option");
        },
      );
  }

  deleteConfigModal(): void {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "Configurable Option",
        data: this.configurableOption,
        overwrittenUrl: `${this.configurableOptionService.basePath}/${this.configurableOption.id}`,
        warningEntity: "configurableOption",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/configurable-options"]);
      }
    });
  }
}
